import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../api/api';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaFacebookF, FaInstagram, FaLinkedin } from 'react-icons/fa';
import { FaTwitter } from 'react-icons/fa6';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in your name, email and message');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/home/contact', form);
      toast.success('Message sent! Our team will get back to you shortly.');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: <FaMapMarkerAlt />, title: 'Our Address', lines: ['Dubai Silicon Oasis', 'Dubai, United Arab Emirates'] },
    { icon: <FaPhone />, title: 'Call Us', lines: ['+971 4 000 0000', 'Mon – Sat, 9am – 7pm'] },
    { icon: <FaEnvelope />, title: 'Email Us', lines: ['support@pikkodubai.com', 'sellers@pikkodubai.com'] },
    { icon: <FaClock />, title: 'Working Hours', lines: ['Sunday – Thursday: 9am – 7pm', 'Friday – Saturday: 10am – 4pm'] },
  ];

  return (
    <div className="bg-[#f5f5f5]">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white border-b border-gray-100">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#E21C1B]/6 blur-3xl -translate-y-1/3 translate-x-1/4 pointer-events-none" />
        <div className="w-[85%] md:w-[90%] sm:w-[95%] mx-auto relative z-10 py-16 sm:py-12 text-center">
          <span className="inline-block bg-[#E21C1B]/10 border border-[#E21C1B]/20 text-[#E21C1B] text-sm font-medium px-4 py-1.5 rounded-full mb-5">
            We'd Love to Hear From You
          </span>
          <h1 className="text-5xl md:text-4xl sm:text-3xl font-extrabold mb-4 leading-tight text-gray-900">
            Get in <span className="text-[#E21C1B]">Touch</span>
          </h1>
          <p className="text-gray-500 text-lg sm:text-base max-w-xl mx-auto leading-relaxed">
            Have a question about an order, a product, or becoming a seller? Our team is here to help.
          </p>
        </div>
      </section>

      {/* Info cards */}
      <section className="py-16">
        <div className="w-[85%] md:w-[90%] sm:w-[95%] mx-auto">
          <div className="grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-6 mb-14">
            {contactInfo.map((item) => (
              <div key={item.title} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200">
                <div className="w-12 h-12 rounded-xl bg-[#E21C1B]/10 text-[#E21C1B] flex items-center justify-center text-lg mb-4">
                  {item.icon}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                {item.lines.map((line) => (
                  <p key={line} className="text-gray-500 text-sm leading-relaxed">{line}</p>
                ))}
              </div>
            ))}
          </div>

          {/* Form + Map */}
          <div className="grid grid-cols-2 md:grid-cols-1 gap-10">
            {/* Form */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Send Us a Message</h2>
              <p className="text-gray-500 text-sm mb-6">Fill out the form below and we'll respond within 24 hours.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#E21C1B] focus:ring-1 focus:ring-[#E21C1B] transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#E21C1B] focus:ring-1 focus:ring-[#E21C1B] transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+971 50 000 0000"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#E21C1B] focus:ring-1 focus:ring-[#E21C1B] transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="Order inquiry"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#E21C1B] focus:ring-1 focus:ring-[#E21C1B] transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Message *</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="How can we help you?"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#E21C1B] focus:ring-1 focus:ring-[#E21C1B] transition resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-6 py-3.5 bg-[#E21C1B] text-white rounded-lg font-bold hover:bg-[#c01717] transition-all duration-200 disabled:opacity-60"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Map + socials */}
            <div className="flex flex-col gap-6">
              <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-100 h-72 sm:h-56">
                <iframe
                  title="Pikko Dubai Location"
                  src="https://www.google.com/maps?q=Dubai+Silicon+Oasis,+Dubai,+UAE&output=embed"
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              <div className="bg-[#1a1a1a] rounded-2xl p-8 text-white flex-1">
                <h3 className="font-bold text-xl mb-2">Prefer to Chat?</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">
                  Reach out on social media or drop us an email — we typically respond within a few hours.
                </p>
                <div className="flex gap-2.5">
                  {[
                    { icon: <FaFacebookF size={13} />, href: '#' },
                    { icon: <FaTwitter size={13} />, href: '#' },
                    { icon: <FaInstagram size={13} />, href: '#' },
                    { icon: <FaLinkedin size={13} />, href: '#' },
                  ].map((s, i) => (
                    <a key={i} href={s.href}
                      className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#E21C1B] transition text-white">
                      {s.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
